import { usePlaceSuggestions } from "@vidario/api-client";
import {
  placeQueryReady,
  standortMessage,
  type PlaceSuggestion,
  type StandortOrigin,
  type WorkModel,
} from "@vidario/domain";
import { Combobox } from "@vidario/ui";
import { Check, MapPin } from "lucide-react";
import { useRef, useState, type ReactElement } from "react";

const HELPER_TONE = { success: "success", warning: "warning", hint: "neutral" } as const;

export interface StandortFieldProps {
  value: string;
  /** The recruiter typed: the place is no longer confirmed. */
  onType: (text: string) => void;
  onPick: (place: PlaceSuggestion) => void;
  /** A suggestion was picked, or the saved job already has coordinates. */
  confirmed: boolean;
  origin: StandortOrigin;
  workModel: WorkModel | null;
  error?: string | undefined;
}

/** The Standort as a place picker (#49): real places from the provider while
 * typing, two lines each so look-alike towns can be told apart. Picking
 * stores the place with its coordinates; typed text that wasn't picked can
 * still be saved — the field says what it costs, it never blocks. */
export function StandortField({
  value,
  onType,
  onPick,
  confirmed,
  origin,
  workModel,
  error,
}: StandortFieldProps): ReactElement {
  const [blurred, setBlurred] = useState(false);
  const [lastPicked, setLastPicked] = useState<PlaceSuggestion | null>(null);
  // Picking also reports the row's label as typed text; that isn't an edit.
  const pickedLabel = useRef<string | null>(null);
  const suggestions = usePlaceSuggestions(value);
  const ready = placeQueryReady(value);
  // While the pause in typing runs, the shown places belong to older text.
  const waiting = ready && (suggestions.isFetching || suggestions.query !== value.trim());
  const message = standortMessage({
    text: value,
    confirmed,
    origin,
    workModel,
    blurred,
    // Two towns can share a name ("Münster"): say which one was picked.
    pickedRegion: lastPicked?.label === value ? lastPicked.region : null,
  });

  const pick = (place: PlaceSuggestion): void => {
    pickedLabel.current = place.label;
    setLastPicked(place);
    onPick(place);
  };

  return (
    <Combobox
      label="Standort"
      required
      icon={<MapPin size={16} />}
      placeholder="z. B. Münster"
      items={ready && !suggestions.isError ? (suggestions.data ?? []) : []}
      itemKey={(p) => `${p.name}|${p.lat}|${p.lng}`}
      itemToString={(p) => p.label}
      renderItem={(p) => (
        <span className="flex flex-col">
          <span className="font-medium text-text-primary">{p.name}</span>
          <span className="text-xs text-text-secondary">
            {[p.region, p.country].filter(Boolean).join(", ")}
          </span>
        </span>
      )}
      inputValue={value}
      onInputValueChange={(next) => {
        if (next === value || next === pickedLabel.current) return;
        pickedLabel.current = null;
        setBlurred(false);
        onType(next);
      }}
      onSelect={pick}
      onBlur={() => setBlurred(true)}
      onEscape={() => {
        if (!confirmed && lastPicked) onPick(lastPicked);
      }}
      selectOnBlur={false}
      // A message that comes and goes mustn't push the fields below (#186).
      reserveHelperLine
      // The open list covers the helper, so it repeats the import's ask (#186).
      {...(origin === "imported" &&
        !confirmed && { listHeader: "Bitte bestätige den Ort aus der Liste" })}
      // An empty field has nothing to suggest yet; typing opens the list.
      openOnFocus={ready}
      highlightFirst={origin === "imported"}
      loading={waiting && !suggestions.isError}
      loadingMessage="Orte werden gesucht …"
      emptyMessage={
        !ready
          ? "Tippe den Ort ein, z. B. Münster."
          : suggestions.isError
            ? "Ortssuche gerade nicht erreichbar. Du kannst den Ort trotzdem eintragen und speichern."
            : "Kein Ort gefunden. Prüfe die Schreibweise oder gib eine größere Stadt in der Nähe ein."
      }
      resultsMessage={(n) => (n === 1 ? "1 Ort gefunden" : `${n} Orte gefunden`)}
      footer="© OpenStreetMap-Mitwirkende"
      endAdornment={confirmed ? <Check size={16} className="text-success-600" /> : undefined}
      {...(message && { helper: message.text, helperTone: HELPER_TONE[message.tone] })}
      {...(error && { error })}
    />
  );
}
